import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Typography
} from '@mui/material';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import config from '../config';

interface AccountSettingsProps {
  open: boolean;
  onClose: () => void;
  isWelcomeDialog?: boolean;
}

interface PrivacySettings {
  nameVisibility: 'full' | 'first' | 'hidden';
}

const AccountSettings: React.FC<AccountSettingsProps> = ({ open, onClose, isWelcomeDialog = false }) => {
  const { getAccessTokenSilently, user } = useAuth0();
  // Initialize display name with Auth0 data if available
  const initialName = (isWelcomeDialog && user) ? (user.name || user.nickname || user.email?.split('@')[0] || '') : '';
  const [displayName, setDisplayName] = useState(initialName);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
    nameVisibility: 'full'
  });

  // Effect to update display name when user data changes
  useEffect(() => {
    if (isWelcomeDialog && user && !displayName) {
      const nameToUse = user.name || user.nickname || user.email?.split('@')[0] || '';
      setDisplayName(nameToUse);
    }
  }, [user, isWelcomeDialog, displayName]);

  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open, isWelcomeDialog, user]);

  const fetchSettings = async () => {
    setIsLoading(true);
    setError(null);

    try {
      let headers = {};
      if (config.auth0.enabled) {
        const token = await getAccessTokenSilently();
        headers = { Authorization: `Bearer ${token}` };
      }

      const response = await axios.get(`${config.api.baseURL}/api/user/settings`, { headers });
      // Only override the display name if we're not in the welcome dialog
      // or if the current display name is empty
      if (!isWelcomeDialog || !displayName) {
        setDisplayName(response.data.displayName);
      }
      
      // Update privacy settings from the response
      if (response.data.privacySettings) {
        setPrivacySettings(response.data.privacySettings);
      }
      
      // Also update localStorage for development mode
      localStorage.setItem('displayName', response.data.displayName);
    } catch (error) {
      console.error('Error fetching settings:', error);
      // In development, fall back to localStorage
      if (!config.auth0.enabled) {
        const savedName = localStorage.getItem('displayName') || '';
        if (!isWelcomeDialog || !displayName) {
          setDisplayName(savedName);
        }
      } else {
        setError('Failed to load settings');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const token = await getAccessTokenSilently();
      
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.post(
        `${config.api.baseURL}/api/user/settings`,
        { 
          displayName,
          privacySettings
        },
        { headers }
      );


      // Update localStorage for development mode
      localStorage.setItem('displayName', displayName);
      
      // Dispatch event for other components
      window.dispatchEvent(new Event('displayNameChanged'));
      onClose();
    } catch (err) {
      const error = err as any;
      console.error('Error saving settings:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setError(error.response?.data?.error || 'Failed to save settings');
      setIsLoading(false);
    }
  };

  if (!isWelcomeDialog) {
    return (
      <Dialog 
        open={open} 
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Account Settings</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your display name"
              disabled={isLoading}
              sx={{ mb: 3 }}
            />

            <FormControl component="fieldset" sx={{ width: '100%' }}>
              <FormLabel component="legend">Name Privacy Settings</FormLabel>
              <RadioGroup
                value={privacySettings.nameVisibility}
                onChange={(e) => setPrivacySettings({
                  ...privacySettings,
                  nameVisibility: e.target.value as 'full' | 'first' | 'hidden'
                })}
              >
                <FormControlLabel 
                  value="full" 
                  control={<Radio />} 
                  label="Show my full name to other users"
                />
                <FormControlLabel 
                  value="first" 
                  control={<Radio />} 
                  label="Only show my first name to other users"
                />
                <FormControlLabel 
                  value="hidden" 
                  control={<Radio />} 
                  label="Don't show my name to other users"
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="primary"
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Welcome to EQ Emblem!</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Typography variant="body1" sx={{ mb: 3 }}>
            This setting will only be relevant if you add a character later
          </Typography>

          <TextField
            fullWidth
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Enter your display name"
            disabled={isLoading}
            sx={{ mb: 3 }}
          />

          <FormControl component="fieldset" sx={{ width: '100%' }}>
            <FormLabel component="legend">Name Privacy Settings</FormLabel>
            <RadioGroup
              value={privacySettings.nameVisibility}
              onChange={(e) => setPrivacySettings({
                ...privacySettings,
                nameVisibility: e.target.value as 'full' | 'first' | 'hidden'
              })}
            >
              <FormControlLabel 
                value="full" 
                control={<Radio />} 
                label="Show my full name to other users"
              />
              <FormControlLabel 
                value="first" 
                control={<Radio />} 
                label="Only show my first name to other users"
              />
              <FormControlLabel 
                value="hidden" 
                control={<Radio />} 
                label="Don't show my name to other users"
              />
            </RadioGroup>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Start Exploring'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountSettings; 